import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import Table from "components/Table/Table.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  menuCell: {
    border: "1px solid #e0e0e0", borderRadius: "6px", padding: "8px 12px",
    background: "#f9fbe7", fontSize: "13px", minWidth: "100px"
  },
  dayLabel: { fontWeight: "bold", color: "#1a237e", fontSize: "13px" }
};

const CATEGORIES = ["Breakfast", "Lunch", "Dinner", "Snack"];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const CATEGORY_TONES = {
  Breakfast: "warning",
  Lunch: "success",
  Dinner: "info",
  Snack: "muted"
};

const STATIC_MENU = {
  Monday:    { Breakfast: "Posho & Beans", Lunch: "Rice & Stew", Dinner: "Matooke & Groundnut Sauce" },
  Tuesday:   { Breakfast: "Bread & Tea", Lunch: "Posho & Beef", Dinner: "Rice & Beans" },
  Wednesday: { Breakfast: "Porridge", Lunch: "Matooke & Beef", Dinner: "Posho & Beans" },
  Thursday:  { Breakfast: "Posho & Beans", Lunch: "Rice & Fish", Dinner: "Matooke & Groundnut Sauce" },
  Friday:    { Breakfast: "Bread & Tea", Lunch: "Posho & Chicken", Dinner: "Rice & Stew" },
  Saturday:  { Breakfast: "Porridge", Lunch: "Special Meal", Dinner: "Rice & Beef" },
  Sunday:    { Breakfast: "Bread & Tea", Lunch: "Special Meal", Dinner: "Posho & Beans" }
};

const BLANK = { mealName: "", category: "Lunch", price: "", description: "" };

class MealManagement extends Component {
  state = {
    meals: [],
    loading: true,
    tab: 0,
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null,
    usingFallback: false
  };

  componentDidMount() { this.fetchMeals(); }

  fetchMeals = async () => {
    this.setState({ loading: true });
    try {
      const res = await api.get("/catering/meals", { params: { limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ meals: Array.isArray(d) ? d : d.data || [], loading: false, usingFallback: false });
    } catch (e) {
      this.setState({ meals: [], loading: false, usingFallback: true });
    }
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  openAdd = () => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null });

  openEdit = meal => {
    this.setState({
      open: true, editing: meal.id, error: null,
      form: {
        mealName: meal.mealName || meal.name || "",
        category: meal.category || "Lunch",
        price: meal.price !== undefined ? String(meal.price) : "",
        description: meal.description || ""
      }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.mealName || !form.category) {
      this.setState({ error: "Meal name and category are required." });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      const payload = { ...form, price: form.price ? parseFloat(form.price) : 0 };
      if (editing) {
        await api.put(`/catering/meals/${editing}`, payload);
      } else {
        await api.post("/catering/meals", payload);
      }
      this.setState({ open: false, saving: false, usingFallback: false });
      this.fetchMeals();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save. Catering endpoint may not be available.", saving: false });
    }
  };

  handleToggleStatus = async meal => {
    const newStatus = meal.status === "available" || !meal.status ? "unavailable" : "available";
    try {
      await api.put(`/catering/meals/${meal.id}`, { ...meal, status: newStatus });
      this.fetchMeals();
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes } = this.props;
    const { meals, loading, tab, open, editing, form, saving, error, usingFallback } = this.state;

    const mealColumns = [
      { id: "mealName", header: "Meal Name", accessorFn: m => m.mealName || m.name || "—" },
      {
        id: "category",
        header: "Category",
        cell: info => {
          const m = info.row.original;
          return <Pill tone={CATEGORY_TONES[m.category] || "muted"}>{m.category || "—"}</Pill>;
        }
      },
      { id: "daysAvailable", header: "Days Available", accessorFn: m => m.daysAvailable || "All days" },
      {
        id: "price",
        header: "Price (UGX)",
        accessorFn: m => (m.price !== undefined ? formatUGX(m.price) : "—"),
        meta: { align: "right" }
      },
      { id: "description", header: "Description", accessorFn: m => m.description || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const m = info.row.original;
          const isAvailable = !m.status || m.status === "available";
          return <Pill tone={isAvailable ? "success" : "danger"}>{isAvailable ? "Available" : "Unavailable"}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const m = info.row.original;
          const isAvailable = !m.status || m.status === "available";
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(m)}>
                <Icon>edit</Icon>
              </Button>
              <Button size="sm" simple onClick={() => this.handleToggleStatus(m)} title={isAvailable ? "Mark Unavailable" : "Mark Available"}>
                <Icon>{isAvailable ? "toggle_on" : "toggle_off"}</Icon>
              </Button>
            </div>
          );
        }
      }
    ];

    const mealPlansEmptyState = (
      <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
        <Icon style={{ fontSize: "48px", color: "#e0e0e0", display: "block", margin: "0 auto 12px" }}>restaurant</Icon>
        <p>No meal plans configured yet.</p>
        <Button size="sm" color="primary" onClick={this.openAdd}>
          Add First Meal
        </Button>
      </div>
    );

    // Weekly menu table data
    const weeklyMenuHead = ["Day", "Breakfast", "Lunch", "Dinner"];
    const weeklyMenuData = DAYS.map(day => [
      <span key={day} className={classes.dayLabel}>{day}</span>,
      <div key={`${day}-b`} className={classes.menuCell}>{STATIC_MENU[day].Breakfast}</div>,
      <div key={`${day}-l`} className={classes.menuCell}>{STATIC_MENU[day].Lunch}</div>,
      <div key={`${day}-d`} className={classes.menuCell}>{STATIC_MENU[day].Dinner}</div>
    ]);

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Catering & Meal Management</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    School cafeteria meal plans and weekly menu
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" onClick={this.openAdd}>
                    <Icon style={{ marginRight: "4px" }}>add</Icon> Add Meal
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              {usingFallback && (
                <div style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: "6px", padding: "10px 14px", marginBottom: "14px", fontSize: "13px", color: "#e65100" }}>
                  <Icon style={{ fontSize: "16px", verticalAlign: "middle", marginRight: "6px" }}>info</Icon>
                  Catering endpoint not available. Showing weekly menu template. Meals added here will sync when the endpoint is ready.
                </div>
              )}

              <Tabs value={tab} onChange={(_, v) => this.setState({ tab: v })} indicatorColor="primary" textColor="primary">
                <Tab label="Meal Plans" />
                <Tab label="Weekly Menu" />
              </Tabs>

              {tab === 0 ? (
                <>
                  <div style={{ margin: "12px 0" }}>
                    <Button size="sm" onClick={this.openAdd} color="primary">
                      + Add Meal Plan
                    </Button>
                  </div>
                  <DataTable
                    columns={mealColumns}
                    data={meals}
                    isLoading={loading}
                    emptyState={mealPlansEmptyState}
                  />
                </>
              ) : loading ? (
                <div style={{ textAlign: "center", padding: "30px" }}><CircularProgress /></div>
              ) : (
                <>
                  <div style={{ margin: "12px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h5 style={{ margin: 0, color: "#1a237e" }}>Weekly Menu Template</h5>
                    <span style={{ fontSize: "12px", color: "#999" }}>Edit in administration settings</span>
                  </div>
                  <Table
                    tableHead={weeklyMenuHead}
                    tableData={weeklyMenuData}
                    customCellClasses={[]} customClassesForCells={[]}
                    customHeadCellClasses={[]} customHeadClassesForCells={[]}
                  />
                </>
              )}
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Meal" : "Add Meal Plan"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <CustomInput labelText="Meal Name *" id="mealName" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.mealName, onChange: this.handleChange("mealName") }} />
            <FormControl className={classes.formControl}>
              <InputLabel>Category *</InputLabel>
              <Select value={form.category} onChange={this.handleChange("category")}>
                {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomInput labelText="Price per Serving (UGX)" id="mealPrice" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.price, onChange: this.handleChange("price"), type: "number", min: "0" }} />
            <div style={{ marginTop: "16px" }}>
              <label style={{ fontSize: "12px", color: "#aaa", textTransform: "uppercase" }}>Description</label>
              <textarea
                rows={3}
                value={form.description}
                onChange={this.handleChange("description")}
                style={{ width: "100%", marginTop: "4px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px", resize: "vertical", boxSizing: "border-box" }}
                placeholder="Ingredients, allergens, serving notes..."
              />
            </div>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : editing ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

MealManagement.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(MealManagement));
